
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";

interface AnalysisScoreChartProps {
  categories: {
    title: string;
    score?: number;
    key: string;
  }[];
}

export const AnalysisScoreChart = ({ categories }: AnalysisScoreChartProps) => {
  // Filter categories that have scores
  const chartData = categories
    .filter(category => typeof category.score === 'number')
    .map(category => ({
      name: category.title,
      score: category.score,
      key: category.key
    }))
    .sort((a, b) => (b.score || 0) - (a.score || 0)); // Sort by score (highest first)

  if (chartData.length === 0) {
    return null;
  }

  // Chart config to define colors - including both light and dark theme values
  const chartConfig = {
    score: {
      theme: {
        light: "#0d2456",  // Blue for light mode
        dark: "#4070d0"    // Lighter blue for dark mode
      }
    }
  };

  return (
    <div className="border-b border-gray-200 bg-white px-4 py-2">
      <h3 className="text-sm font-medium text-gray-700 mb-1">Category Scores Summary</h3>
      <div className="h-[180px] w-full">
        <ChartContainer config={chartConfig}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis 
                type="number" 
                domain={[0, 100]} 
                tickCount={6} 
                tickFormatter={(value) => `${value}%`}
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={100}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-2 border border-gray-200 shadow-md rounded-md">
                        <p className="font-medium">{data.name}</p>
                        <p className="text-[#0d2456] font-bold">{`${Math.round(data.score)}%`}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar 
                dataKey="score" 
                fill="var(--color-score)" 
                radius={[0, 4, 4, 0]} 
                barSize={14}
                name="Score" 
                label={{ 
                  position: 'right', 
                  formatter: (value: number) => `${Math.round(value)}%`,
                  fill: '#0d2456',
                  fontSize: 11,
                  fontWeight: 500
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
};
